import { Direction } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  AbstractControl,
} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { FormValidators } from 'src/app/@forms/validators/validators';
import { PopUpComponent } from 'src/app/@shared/components/pop-ups/pop-up.component';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { POPUP_DATA } from 'src/app/@shared/services/modal/modal.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { FAVORITE_TYPES } from 'src/app/modules/route-planner/constants/favorite-types.constants';
import {
  ADD_FAVORITE,
  CANCEL,
  CLOSE,
  DESTINATION,
  EDIT_FAVORITE,
  FAVORITE_TYPE,
  INSERT_LINE_NUMBER_AND_CITY,
  LINE,
  LINE_NUMBER_AND_CITY,
  LINE_SEARCHING,
  NEW_FAVORITE,
  ORIGIN,
  ROUTE,
  SAVE_FAVORITE,
  SEARCH_BY,
  STATION,
  STATION_CODE,
  STATION_NAME_NUMBER_ADDRESS,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eFavoriteType } from 'src/app/modules/route-planner/enums/favorite/favorite-type.enum';
import { FavoriteLine } from 'src/app/modules/route-planner/interfaces/favorites/favorite-line.interface';
import { FavoriteLines } from 'src/app/modules/route-planner/interfaces/favorites/favorite-lines.interface';
import { FavoriteRoute } from 'src/app/modules/route-planner/interfaces/favorites/favorite-route.interface';
import { FavoriteStation } from 'src/app/modules/route-planner/interfaces/favorites/favorite-station.interface';
import { FavoriteStations } from 'src/app/modules/route-planner/interfaces/favorites/favorite-stations.interface';
import { Favorite } from 'src/app/modules/route-planner/interfaces/favorites/favorite.interface';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';
import { SourceDestinationForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/source-destination-form';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { ScheduleService } from 'src/app/modules/route-planner/services/schedule/schedule.service';

@Component({
    selector: 'app-edit-favorite-pop-up',
    templateUrl: './edit-favorite-pop-up.component.html',
    styleUrls: ['./edit-favorite-pop-up.component.scss'],
    standalone: false
})
export class EditFavoritePopUpComponent
  extends PopUpComponent
  implements OnInit {
  private _searchSub: Subscription;
  searchSubject$ = new Subject<string>();

  private destroy$ = new Subject<void>();

  favoriteForm: FormGroup;
  formContext: FormGroup;

  favoriteTypes$ = of(FAVORITE_TYPES);
  eFavoriteType = eFavoriteType;

  autoCompleteOptions$: Observable<SelectOption<any>[]> = of([]);

  originCompleteOptions$: Observable<SelectOption<any>[]> = of([]);
  destinationCompleteOptions$: Observable<SelectOption<any>[]> = of([]);

  newFavoriteText: string
  editFavoriteText: string
  favoriteTypeText: string
  addFavoriteText: string
  saveFavoriteText: string
  cancelText: string
  closeText: string
  originText: string
  destinationText: string
  SearchStationText: string
  stationCodeText: string
  searchLineText: string
  searchByStationLabelText: string
  searchByLineNumberAndCityText: string

  direction: Direction

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,

    @Inject(POPUP_DATA)
    public readonly data: { favorite?: Favorite },
    private readonly fb: FormBuilder,
    private readonly languageService: LanguageService,
    private readonly autoCompleteService: AutoCompleteService,
    private readonly favoritesService: FavoritesService,
    private readonly scheduleService: ScheduleService
  ) {
    super(routePlanningService, resizeService, overlay);

    this.favoriteForm = this.fb.group({
      id: new FormControl<string>(this.data.favorite?.id),
      type: new FormControl<eFavoriteType>(
        {
          value: data.favorite?.type ?? eFavoriteType.ROUTES,
          disabled: !!data.favorite,
        },
        Validators.required
      ),
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.favoriteForm.get('type')?.valueChanges.subscribe((type) => {
      this.formContext = this.initializeForm(type, this.data.favorite);
    });

    this.initializeMultiLanguageTexts();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction
      this.originText = ORIGIN[language.language]
      this.destinationText = DESTINATION[language.language]
      this.newFavoriteText = NEW_FAVORITE[language.language]
      this.editFavoriteText = EDIT_FAVORITE[language.language]
      this.favoriteTypeText = FAVORITE_TYPE[language.language]
      this.cancelText = CANCEL[language.language]
      this.closeText = CLOSE[language.language]
      this.addFavoriteText = ADD_FAVORITE[language.language]
      this.saveFavoriteText = SAVE_FAVORITE[language.language]
      this.SearchStationText = STATION[language.language]
      this.stationCodeText = STATION_CODE[language.language]
      this.searchLineText = LINE_SEARCHING[language.language]
      this.searchByStationLabelText = STATION_NAME_NUMBER_ADDRESS[language.language]
      this.searchByLineNumberAndCityText = LINE_NUMBER_AND_CITY[language.language]

      const favoriteTypes = [...FAVORITE_TYPES];
      favoriteTypes.forEach((type) => {
        switch (type.value) {
          case eFavoriteType.LINES:
            type.label = LINE[language.language];
            break;
          case eFavoriteType.STATIONS:
            type.label = STATION[language.language];
            break;
          case eFavoriteType.ROUTES:
            type.label = ROUTE[language.language];
            break;
        }
      });
      this.favoriteTypes$ = of(favoriteTypes);
    });
  }

  initializeForm(favoriteType: eFavoriteType, initialValue?: any): FormGroup {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();

    switch (favoriteType) {
      case eFavoriteType.ROUTES:
        return this.getRouteForm(initialValue);
      case eFavoriteType.LINES:
        return this.getLineForm(initialValue);
      case eFavoriteType.STATIONS:
        return this.getStationForm(initialValue);
    }

    return this.favoriteForm;
  }

  saveFavorite(favoriteType: eFavoriteType) {
    switch (favoriteType) {
      case eFavoriteType.ROUTES:
        this.saveFavoriteRoute();
        break;
      case eFavoriteType.LINES:
        this.saveFavoriteLine();
        break;
      case eFavoriteType.STATIONS:
        this.saveFavoriteStation();
        break;
    }
  }

  addFavorite(favoriteType: eFavoriteType) {
    switch (favoriteType) {
      case eFavoriteType.ROUTES:
        this.addFavoriteRoute();
        break;
      case eFavoriteType.LINES:
        this.addFavoriteLine();
        break;
      case eFavoriteType.STATIONS:
        this.addFavoriteStation();
    }
  }

  getRouteForm(initialValue?: FavoriteRoute) {
    this.formContext = this.fb.group({
      ...this.favoriteForm.controls,
      formValues: this.fb.group({
        origin: new FormControl<Address>(
          initialValue?.formValues?.origin,
          [FormValidators.required, FormValidators.validateNotString()]
        ),
        destination: new FormControl<Address>(
          initialValue?.formValues?.destination,
          [FormValidators.required, FormValidators.validateNotString()]
        ),
      }),
    });

    this.originCompleteOptions$  = this.getNestedGroup('formValues')
      .controls['origin'].valueChanges.pipe(
        debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
        distinctUntilChanged(),
        switchMap((val: string) => {
          if (!!val) {
            return this.autoCompleteService.searchLocations(val);
          }

          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      // .subscribe((addresses) => (this.originCompleteOptions$ = of(addresses)));

      this.destinationCompleteOptions$ = this.getNestedGroup('formValues')
      .controls['destination'].valueChanges.pipe(
        debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
        distinctUntilChanged(),
        switchMap((val: string) => {
          if (!!val) {
            return this.autoCompleteService.searchLocations(val);
          }

          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      // .subscribe(
      //   (addresses) => (this.destinationCompleteOptions$ = of(addresses))
      // );

    return this.formContext;
  }

  addFavoriteRoute() {
    const favoriteRoute: FavoriteRoute = {
      ...this.formContext.getRawValue(),
      route: {
        specialMessages: {
          routeMessages: [],
          stopMessages: [],
        },
      },
      type: eFavoriteType.ROUTES,
    };

    this.favoritesService.addNewFavorite(favoriteRoute);
  }

  saveFavoriteRoute() {
    const favoriteFormRoute = this.formContext.getRawValue();
    const favoriteRoutes: FavoriteRoute[] =
      this.favoritesService.favorites.routes;
    const favoriteRouteIndex = favoriteRoutes.findIndex(
      (route) => route.id == favoriteFormRoute.id
    );
    const favoriteRoute: FavoriteRoute = favoriteRoutes[favoriteRouteIndex];

    if (favoriteRouteIndex !== -1) {
      this.favoritesService.removeFavorite(favoriteRoutes[favoriteRouteIndex]);
      this.favoritesService.addNewFavorite({
        ...favoriteRoute,
        ...favoriteFormRoute,
      });
    }
  }

  getStationForm(initialValue?: Stop) {
    if (!!this._searchSub) {
      this._searchSub.unsubscribe();
    }

    this.formContext = this.fb.group({
      ...this.favoriteForm.controls,
      stop: new FormControl<Stop>(initialValue, [FormValidators.required, FormValidators.validateNotString()]),
    });

    this._searchSub = this.searchSubject$
      .pipe(
        debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
        distinctUntilChanged(),
        switchMap((val: string) => {
          if (!!val) {
            return this.autoCompleteService.searchStations(val);
          }

          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.autoCompleteOptions$ = of(results);
      });

    return this.formContext;
  }

  addFavoriteStation() {
    const favoriteStation: Stop = this.formContext.controls['stop'].value;
    const favoriteStations = this.favoritesService.favorites?.stations;
    const newStations = favoriteStations?.stations
      ? [...favoriteStations?.stations]
      : [];

    if (
      !newStations.find(
        (station) => station.stopcode === favoriteStation.stopcode
      )
    ) {
      newStations.push({
        ...favoriteStation,
        type: eFavoriteType.STATIONS,
      });
    }

    const newFavoriteStations: FavoriteStations = {
      ...favoriteStations,
      stations: newStations,
      type: eFavoriteType.STATIONS,
    };

    this.favoritesService.updateFavorite(newFavoriteStations);
    this.closeCard();
  }

  saveFavoriteStation() {
    const newFavorite: FavoriteStation =
      this.formContext.controls['stop'].value;
    const favoriteStations = this.favoritesService.favorites.stations;
    const favoriteIndex = favoriteStations.stations.findIndex(
      (stop) => stop.id === this.data.favorite.id
    );

    if (favoriteIndex !== -1) {
      favoriteStations.stations[favoriteIndex] = {
        ...newFavorite,
        type: eFavoriteType.STATIONS,
      };

      this.favoritesService.updateFavorite(favoriteStations);
    }
  }

  getLineForm(initialValue?: Route) {
    if (!!this._searchSub) {
      this._searchSub.unsubscribe();
    }

    this.formContext = this.fb.group({
      ...this.favoriteForm.controls,
      line: new FormControl<Route>(initialValue, [FormValidators.required, FormValidators.validateNotString()]),
    });

    this._searchSub = this.searchSubject$
      .pipe(
        debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
        distinctUntilChanged(),
        switchMap((val: string) => this.autoCompleteService.searchLines(val)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.autoCompleteOptions$ = of(results);
      });

    return this.formContext;
  }

  setLineValue(line: Route) {
    this.scheduleService
      .getRouteStops(line.routeId, line.oppositeDirectionId)
      .subscribe((res) =>
        this.formContext.patchValue(
          {
            line: { ...res.data.route },
          },
          { emitEvent: false }
        )
      );
  }

  addFavoriteLine() {
    const favoriteLine: Route = this.formContext.controls['line'].value;
    const favoriteLines = this.favoritesService.favorites?.lines;
    const newLines = favoriteLines?.routes ? [...favoriteLines?.routes] : [];

    if (!newLines.find((line) => line.routeId === favoriteLine.routeId)) {
      newLines.push({
        ...favoriteLine,
        type: eFavoriteType.LINES,
      });
    }

    const newFavoriteLines: FavoriteLines = {
      ...favoriteLines,
      routes: newLines,
      type: eFavoriteType.LINES,
    };

    this.favoritesService.updateFavorite(newFavoriteLines);
  }

  saveFavoriteLine() {
    const newFavorite: FavoriteLine = this.formContext.controls['line'].value;
    const favoriteLines = this.favoritesService.favorites.lines;
    const favoriteIndex = favoriteLines.routes.findIndex(
      (line) => line.id === this.data.favorite.id
    );

    if (favoriteIndex !== -1) {
      favoriteLines.routes[favoriteIndex] = {
        ...newFavorite,
        type: eFavoriteType.LINES,
      };

      this.favoritesService.updateFavorite(favoriteLines);
    }
  }

  getNestedGroup(groupName: string): FormGroup {
    return this.formContext.get(groupName) as FormGroup;
  }
}
